import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Typography, Table, Card, CardContent, CardActions,
    Modal, ModalClose, ModalDialog, FormControl, FormLabel, Input, Textarea,
    Select, Option, IconButton, Divider, CircularProgress, Alert, Snackbar
} from '@mui/joy';
import axios from 'axios';
import {
    FileDownload, Preview, Send, Add, Edit, Delete,
    CheckCircle, Warning, ContentCopy
} from '@mui/icons-material';
import { workshopDomain } from '../../util/domain';

const CertificateManagement = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [certificates, setCertificates] = useState([]);
    const [attendanceStats, setAttendanceStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [templateDialog, setTemplateDialog] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [bulkGenerateDialog, setBulkGenerateDialog] = useState(false);
    const [sendEmailsDialog, setSendEmailsDialog] = useState(false);
    const [duplicateDialog, setDuplicateDialog] = useState(null);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [newTemplate, setNewTemplate] = useState({
        name: '',
        html_template: '',
        storage_method: 'database',
        certificate_type: '',
        certificate_title: '',
        certificate_date: ''
    });

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/admin');
            return;
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');
        // Check if user has Admin role
        const roles = user.roles || [];
        const isAdmin = roles.includes('Admin');
        if (!isAdmin) {
            navigate('/admin/home');
            return;
        }
        fetchEvents();
        fetchTemplates();
    }, []);

    useEffect(() => {
        if (selectedEvent) {
            fetchAttendanceStats();
            fetchCertificates();
        }
    }, [selectedEvent]);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${workshopDomain}/api/events/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setEvents(response.data.events);
            }
        } catch (error) {
            console.error('Error fetching events:', error);
            showNotification('Failed to fetch events', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchTemplates = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${workshopDomain}/api/certificate/templates`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setTemplates(response.data.templates);
                if (response.data.templates.length > 0 && !selectedTemplate) {
                    setSelectedTemplate(response.data.templates[0].id);
                }
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
            showNotification('Failed to fetch certificate templates', 'error');
        }
    };

    const fetchAttendanceStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${workshopDomain}/api/certificate/attendance-stats/${selectedEvent.hash_id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setAttendanceStats(response.data.completedCount);
            }
        } catch (error) {
            console.error('Error fetching attendance stats:', error);
        }
    };

    const fetchCertificates = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${workshopDomain}/api/certificate/event/${selectedEvent.hash_id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setCertificates(response.data.certificates);
            }
        } catch (error) {
            console.error('Error fetching certificates:', error);
            showNotification('Failed to fetch certificates', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEventSelect = (event) => {
        setSelectedEvent(event);
    };

    const handleCreateTemplate = async () => {
        try {
            if (!newTemplate.name || !newTemplate.html_template || !newTemplate.certificate_type ||
                !newTemplate.certificate_title || !newTemplate.certificate_date) {
                showNotification('All fields are required', 'error');
                return;
            }

            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.post(`${workshopDomain}/api/certificate/templates`, newTemplate, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                showNotification('Certificate template created successfully', 'success');
                setTemplateDialog(false);
                setNewTemplate({
                    name: '',
                    html_template: '',
                    storage_method: 'database',
                    certificate_type: '',
                    certificate_title: '',
                    certificate_date: ''
                });
                fetchTemplates();
            }
        } catch (error) {
            console.error('Error creating template:', error);
            showNotification(error.response?.data?.message || 'Failed to create certificate template', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateTemplate = async () => {
        try {
            if (!editingTemplate) return;

            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.put(`${workshopDomain}/api/certificate/templates/${editingTemplate.id}`, editingTemplate, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                showNotification('Template updated successfully', 'success');
                setTemplateDialog(false);
                setEditingTemplate(null);
                fetchTemplates();
            }
        } catch (error) {
            console.error('Error updating template:', error);
            showNotification(error.response?.data?.message || 'Failed to update template', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handlePreviewTemplate = (templateId) => {
        if (!selectedEvent) {
            showNotification('Please select an event first', 'warning');
            return;
        }
        const previewUrl = `${workshopDomain}/api/certificate/preview/${selectedEvent.hash_id}/${templateId}`;
        window.open(previewUrl, '_blank');
    };

    const handleGenerateBulk = async () => {
        if (!selectedEvent || !selectedTemplate) {
            showNotification('Please select an event and template', 'warning');
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${workshopDomain}/api/certificate/generate-bulk/${selectedEvent.hash_id}`,
                { templateId: selectedTemplate },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                showNotification(`${response.data.certificates.length} certificates generated successfully`, 'success');
                fetchCertificates();
                setBulkGenerateDialog(false);
            } else {
                showNotification(response.data.message, 'warning');
            }
        } catch (error) {
            console.error('Error generating certificates:', error);
            showNotification(error.response?.data?.message || 'Failed to generate certificates', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSendEmails = async () => {
        if (!selectedEvent) {
            showNotification('Please select an event', 'warning');
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${workshopDomain}/api/certificate/send-emails/${selectedEvent.hash_id}`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                showNotification(response.data.message, 'success');
                setSendEmailsDialog(false);
                // Refresh certificates after a delay to see updated email_sent status
                setTimeout(() => {
                    fetchCertificates();
                }, 2000);
            }
        } catch (error) {
            console.error('Error sending emails:', error);
            showNotification(error.response?.data?.message || 'Failed to send emails', 'error');
        } finally {
            setLoading(false);
        }
    };

    const openEditTemplate = (template) => {
        setEditingTemplate({ ...template });
        setTemplateDialog(true);
    };

    const handleDuplicateTemplate = async () => {
        if (!duplicateDialog) return;

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${workshopDomain}/api/certificate/templates/${duplicateDialog.id}/duplicate`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                showNotification(`Template duplicated as "${response.data.template?.name || duplicateDialog.name + ' (Copy)'}"`, 'success');
                setDuplicateDialog(null);
                fetchTemplates();
                if (response.data.templateId) {
                    setSelectedTemplate(response.data.templateId);
                }
            }
        } catch (error) {
            console.error('Error duplicating template:', error);
            showNotification(error.response?.data?.message || 'Failed to duplicate template', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    const closeNotification = () => {
        setNotification({ ...notification, open: false });
    };

    // Load certificate-portrait.html as default template
    const loadDefaultTemplate = async () => {
        try {
            const response = await fetch('/certificate-portrait.html');
            const html = await response.text();
            setNewTemplate(prev => ({ ...prev, html_template: html }));
        } catch (error) {
            console.error('Error loading default template:', error);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography level="h2" sx={{ mb: 3 }}>Certificate Management</Typography>

            {/* Templates Section */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography level="h3">Templates</Typography>
                <Button
                    startDecorator={<Add />}
                    onClick={() => {
                        setEditingTemplate(null);
                        setTemplateDialog(true);
                    }}
                    sx={{ bgcolor: '#ea7a23', '&:hover': { bgcolor: '#d66c1b' } }}
                >
                    Create Template
                </Button>
            </Box>

            <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {templates.length > 0 ? (
                    templates.map(template => (
                        <Card
                            key={template.id}
                            sx={{
                                width: 250,
                                border: selectedTemplate === template.id ? '2px solid #ea7a23' : 'none',
                                cursor: 'pointer'
                            }}
                            onClick={() => setSelectedTemplate(template.id)}
                        >
                            <CardContent>
                                <Typography level="title-md">{template.name}</Typography>
                                <Typography level="body-xs" sx={{ mt: 1 }}>
                                    Type: {template.certificate_type}
                                </Typography>
                            </CardContent>
                            <CardActions>
                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="primary"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handlePreviewTemplate(template.id);
                                    }}
                                >
                                    <Preview />
                                </IconButton>
                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openEditTemplate(template);
                                    }}
                                >
                                    <Edit />
                                </IconButton>
                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDuplicateDialog(template);
                                    }}
                                >
                                    <ContentCopy />
                                </IconButton>
                            </CardActions>
                        </Card>
                    ))
                ) : (
                    <Alert color="warning">No templates found. Create one to get started.</Alert>
                )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Events Section */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography level="h3">Events</Typography>
                {selectedEvent && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            startDecorator={<Add />}
                            onClick={() => setBulkGenerateDialog(true)}
                            disabled={!selectedTemplate}
                            sx={{ bgcolor: selectedTemplate ? '#ea7a23' : 'inherit', '&:hover': { bgcolor: '#d66c1b' } }}
                        >
                            Generate Certificates
                        </Button>
                        <Button
                            startDecorator={<Send />}
                            onClick={() => setSendEmailsDialog(true)}
                            disabled={certificates.length === 0}
                            sx={{ bgcolor: certificates.length > 0 ? '#2e7d32' : 'inherit', '&:hover': { bgcolor: '#1b5e20' } }}
                        >
                            Send Certificates
                        </Button>
                    </Box>
                )}
            </Box>

            <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {events.length > 0 ? (
                    events.map(event => (
                        <Card
                            key={event.hash_id}
                            sx={{
                                width: 300,
                                border: selectedEvent?.hash_id === event.hash_id ? '2px solid #ea7a23' : 'none',
                                cursor: 'pointer'
                            }}
                            onClick={() => handleEventSelect(event)}
                        >
                            <CardContent>
                                <Typography level="title-lg">{event.title}</Typography>
                                <Typography level="body-sm">
                                    Date: {new Date(event.event_start_date).toLocaleDateString()}
                                </Typography>
                                <Typography level="body-sm">
                                    Type: {event.event_type}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))
                ) : (
                    <Alert color="warning">No events found.</Alert>
                )}
            </Box>

            {selectedEvent && (
                <>
                    <Divider sx={{ my: 3 }} />

                    {/* Attendance Stats */}
                    {attendanceStats !== null && (
                        <Alert color="info" sx={{ mb: 3 }}>
                            <Typography level="body-md">
                                Completed Attendees: <strong>{attendanceStats}</strong>
                            </Typography>
                        </Alert>
                    )}

                    {/* Certificates List */}
                    <Box sx={{ mb: 3 }}>
                        <Typography level="h3" sx={{ mb: 2 }}>
                            Certificates for: {selectedEvent.title}
                        </Typography>

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : certificates.length > 0 ? (
                            <Table stickyHeader>
                                <thead>
                                    <tr>
                                        <th>Certificate ID</th>
                                        <th>Student Name</th>
                                        <th>Issue Date</th>
                                        <th>Status</th>
                                        <th>Email Sent</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {certificates.map(cert => (
                                        <tr key={cert.id}>
                                            <td>{cert.certificate_id}</td>
                                            <td>{cert.student_name}</td>
                                            <td>{new Date(cert.issue_date).toLocaleDateString()}</td>
                                            <td>
                                                <Typography level="body-sm" color={cert.certificate_status === 'issued' ? 'success' : 'neutral'}>
                                                    {cert.certificate_status}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm" color={cert.email_sent ? 'success' : 'neutral'}>
                                                    {cert.email_sent ? 'Yes' : 'No'}
                                                </Typography>
                                            </td>
                                            <td>
                                                <IconButton
                                                    size="sm"
                                                    color="primary"
                                                    onClick={() => {
                                                        window.open(`${workshopDomain}/c/${cert.certificate_id}/download`, '_blank');
                                                    }}
                                                >
                                                    <FileDownload />
                                                </IconButton>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <Alert color="neutral">
                                No certificates have been generated for this event yet.
                                Click "Generate Certificates" to create them.
                            </Alert>
                        )}
                    </Box>
                </>
            )}

            {/* Create/Edit Template Dialog */}
            <Modal open={templateDialog} onClose={() => {
                setTemplateDialog(false);
                setEditingTemplate(null);
                setNewTemplate({
                    name: '',
                    html_template: '',
                    storage_method: 'database',
                    certificate_type: '',
                    certificate_title: '',
                    certificate_date: ''
                });
            }}>
                <ModalDialog
                    aria-labelledby="template-dialog-title"
                    sx={{ maxWidth: 900, width: '100%', maxHeight: '90vh', overflow: 'auto' }}
                >
                    <ModalClose />
                    <Typography id="template-dialog-title" level="h2">
                        {editingTemplate ? 'Edit' : 'Create'} Certificate Template
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                        <FormControl sx={{ mb: 2 }}>
                            <FormLabel>Template Name</FormLabel>
                            <Input
                                value={editingTemplate?.name || newTemplate.name}
                                onChange={e => editingTemplate
                                    ? setEditingTemplate({ ...editingTemplate, name: e.target.value })
                                    : setNewTemplate({ ...newTemplate, name: e.target.value })}
                                placeholder="e.g., Workshop Certificate Template"
                            />
                        </FormControl>

                        <FormControl sx={{ mb: 2 }}>
                            <FormLabel>Storage Method</FormLabel>
                            <Select
                                value={editingTemplate?.storage_method || newTemplate.storage_method}
                                onChange={(_, newValue) => editingTemplate
                                    ? setEditingTemplate({ ...editingTemplate, storage_method: newValue })
                                    : setNewTemplate({ ...newTemplate, storage_method: newValue })}
                            >
                                <Option value="database">Database</Option>
                                <Option value="file">File</Option>
                            </Select>
                        </FormControl>

                        <FormControl sx={{ mb: 2 }}>
                            <FormLabel>Certificate Type</FormLabel>
                            <Input
                                value={editingTemplate?.certificate_type || newTemplate.certificate_type}
                                onChange={e => editingTemplate
                                    ? setEditingTemplate({ ...editingTemplate, certificate_type: e.target.value })
                                    : setNewTemplate({ ...newTemplate, certificate_type: e.target.value })}
                                placeholder="e.g., Certificate of Participation"
                            />
                        </FormControl>

                        <FormControl sx={{ mb: 2 }}>
                            <FormLabel>Certificate Title</FormLabel>
                            <Input
                                value={editingTemplate?.certificate_title || newTemplate.certificate_title}
                                onChange={e => editingTemplate
                                    ? setEditingTemplate({ ...editingTemplate, certificate_title: e.target.value })
                                    : setNewTemplate({ ...newTemplate, certificate_title: e.target.value })}
                                placeholder="e.g., Python Programming Workshop"
                            />
                        </FormControl>

                        <FormControl sx={{ mb: 2 }}>
                            <FormLabel>Certificate Date</FormLabel>
                            <Input
                                value={editingTemplate?.certificate_date || newTemplate.certificate_date}
                                onChange={e => editingTemplate
                                    ? setEditingTemplate({ ...editingTemplate, certificate_date: e.target.value })
                                    : setNewTemplate({ ...newTemplate, certificate_date: e.target.value })}
                                placeholder="e.g., January 15, 2024"
                            />
                        </FormControl>

                        <FormControl sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <FormLabel>HTML Template</FormLabel>
                                {!editingTemplate && (
                                    <Button size="sm" variant="outlined" onClick={loadDefaultTemplate}>
                                        Load Default Template
                                    </Button>
                                )}
                            </Box>
                            <Textarea
                                value={editingTemplate?.html_template || newTemplate.html_template}
                                onChange={e => editingTemplate
                                    ? setEditingTemplate({ ...editingTemplate, html_template: e.target.value })
                                    : setNewTemplate({ ...newTemplate, html_template: e.target.value })}
                                minRows={15}
                                placeholder="HTML template with placeholders: {{certificateType}}, {{studentName}}, {{certificateDate}}, {{certificateTitle}}, {{qrCodeImageData}}"
                            />
                            <Typography level="body-xs" sx={{ mt: 1 }}>
                                Available placeholders: {"{{certificateType}}"}, {"{{studentName}}"}, {"{{certificateDate}}"},
                                {"{{certificateTitle}}"}, {"{{qrCodeImageData}}"}
                            </Typography>
                        </FormControl>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                color="neutral"
                                onClick={() => {
                                    setTemplateDialog(false);
                                    setEditingTemplate(null);
                                    setNewTemplate({
                                        name: '',
                                        html_template: '',
                                        storage_method: 'database',
                                        certificate_type: '',
                                        certificate_title: '',
                                        certificate_date: ''
                                    });
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}
                                loading={loading}
                                sx={{ bgcolor: '#ea7a23', '&:hover': { bgcolor: '#d66c1b' } }}
                            >
                                {editingTemplate ? 'Update' : 'Create'} Template
                            </Button>
                        </Box>
                    </Box>
                </ModalDialog>
            </Modal>

            {/* Bulk Generate Dialog */}
            <Modal open={bulkGenerateDialog} onClose={() => setBulkGenerateDialog(false)}>
                <ModalDialog>
                    <ModalClose />
                    <Typography level="h2">Generate Certificates</Typography>

                    <Typography level="body-md" sx={{ mt: 2, mb: 3 }}>
                        This will generate certificates for all students with attendance status "COMPLETED" for the event "{selectedEvent?.title}".
                        Certificates will only be generated for students who don't already have one.
                    </Typography>

                    {attendanceStats !== null && (
                        <Alert color="info" sx={{ mb: 3 }}>
                            <Typography level="body-sm">
                                {attendanceStats} students have completed attendance and are eligible for certificates.
                            </Typography>
                        </Alert>
                    )}

                    <FormControl sx={{ mb: 3 }}>
                        <FormLabel>Certificate Template</FormLabel>
                        <Select
                            value={selectedTemplate}
                            onChange={(_, newValue) => setSelectedTemplate(newValue)}
                        >
                            {templates.map(template => (
                                <Option key={template.id} value={template.id}>
                                    {template.name}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            color="neutral"
                            onClick={() => setBulkGenerateDialog(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleGenerateBulk}
                            loading={loading}
                            sx={{ bgcolor: '#ea7a23', '&:hover': { bgcolor: '#d66c1b' } }}
                        >
                            Generate Certificates
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>

            {/* Send Emails Dialog */}
            <Modal open={sendEmailsDialog} onClose={() => setSendEmailsDialog(false)}>
                <ModalDialog>
                    <ModalClose />
                    <Typography level="h2">Send Certificates via Email</Typography>

                    <Typography level="body-md" sx={{ mt: 2, mb: 3 }}>
                        This will send certificate download links to all students via email.
                        Emails will be sent with a 5-second delay between each email.
                    </Typography>

                    <Alert color="warning" sx={{ mb: 3 }}>
                        <Typography level="body-sm">
                            This process may take some time. You can close this dialog and the emails will continue sending in the background.
                        </Typography>
                    </Alert>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            color="neutral"
                            onClick={() => setSendEmailsDialog(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSendEmails}
                            loading={loading}
                            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                        >
                            Send Emails
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>

            {/* Duplicate Template Dialog */}
            <Modal open={!!duplicateDialog} onClose={() => setDuplicateDialog(null)}>
                <ModalDialog>
                    <ModalClose />
                    <Typography level="h2">Duplicate Template</Typography>

                    <Typography level="body-md" sx={{ mt: 2, mb: 3 }}>
                        Duplicate &quot;{duplicateDialog?.name}&quot;? A new template named &quot;{duplicateDialog?.name} (Copy)&quot; will be created.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            color="neutral"
                            onClick={() => setDuplicateDialog(null)}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDuplicateTemplate}
                            loading={loading}
                            sx={{ bgcolor: '#ea7a23', '&:hover': { bgcolor: '#d66c1b' } }}
                        >
                            Duplicate
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>

            {/* Notification */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={closeNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    variant="filled"
                    color={notification.severity}
                    onClose={closeNotification}
                    startDecorator={
                        notification.severity === 'success' ? <CheckCircle /> :
                            notification.severity === 'error' ? <Warning /> : null
                    }
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CertificateManagement;
